import { useQuery } from '@tanstack/react-query';
import { ChartId, GetChartUsageResponse } from '@blisspointmedia/bpm-types/dist/dashboarding';
import { awaitJSON, DashboardingLambdaFetch } from '../../../utils/fetch-utils';

// A chart's "blast radius": which dashboards reference it and whether the requesting user owns them.
// Because charts are shared by reference, an in-place Update changes the chart on every dashboard that
// references it — so before promoting an edit we fetch this to (a) show the persistent "used in N
// places" scope cue and (b) decide whether to interpose the fan-out confirm. `enabled` is passed by
// the caller so the query only fires when it's meaningful: there's an unsaved edit on a SAVED chart
// (never for a brand-new draft, which is on no dashboard yet).
export const useChartUsage = (company: string, chartId: ChartId | null, enabled: boolean) =>
  useQuery<GetChartUsageResponse>({
    queryKey: ['dashboarding-chart-usage', company, chartId],
    enabled: Boolean(company && chartId) && enabled,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      if (!chartId) throw new Error('chartId required'); // unreachable — `enabled` guards
      const res = await DashboardingLambdaFetch('/getChartUsage', {
        params: { company, id: chartId }
      });
      return awaitJSON<GetChartUsageResponse>(res);
    }
  });

// The blast-radius test (§6): warn whenever the chart is on ANY dashboard the requesting user does not
// solely own — even one shared/other-owned dashboard — independent of the raw count. A chart on only
// the user's own dashboards updates silently. Undefined usage (still loading / errored) does not warn.
//
// `count` is the TRUE number of dashboards holding this chart; `dashboards` names only the ones the
// caller may open. Once the backend filters that array, a chart sitting on a dashboard the caller
// cannot see would stop triggering the warning — for exactly the users the warning protects, since
// they are the ones editing something with reach they cannot see. So compare the two.
//
// `count > dashboards.length` cannot fire wrongly for a chart placed twice on ONE dashboard: the
// backend query collapses duplicates with `group by id, name, created_by`, so `count` counts
// dashboards, not cards.
export const chartFansOut = (usage: GetChartUsageResponse | undefined): boolean =>
  Boolean(
    usage &&
    (usage.count > usage.dashboards.length || usage.dashboards.some(d => !d.ownedByRequester))
  );
